/**
 * The 5-model wind-comparison sheet — ported from `combo_wind_chart.dart`. A
 * dark, buoy-style pull-up popup (drag handle + header + close) over a canvas
 * line chart: 5 model series (null gaps break the line), a y-axis snapped to 5s,
 * 6-hour gridlines, a scrub crosshair + per-time tooltip, and horizontal scroll
 * over the ~10-day extent. Loading / "warming up" + Retry / no-data states.
 *
 * Driven by `ComboService` (windModels / windModelsLoading / windModelsError).
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { ComboService, ComboState } from './combo.service';
import { ModelSeries, WindModelData } from './combo-wind';
import { parseUtc } from './combo-time';
import { UserSettingsService } from '../../../../services/user-settings.service';
import { speedFromMs } from '../../../../shared/utils/unit-formatter.util';

const K_CHART_HEIGHT = 260;
const K_LM = 42;
const K_RM = 14;
const K_TM = 10;
const K_BM = 22;
const K_STEP_PX = 14;

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLbl(t: string): string {
  return WD[parseUtc(t).getUTCDay()];
}
function timeLbl(t: string): string {
  const d = parseUtc(t);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${WD[d.getUTCDay()]} ${hh}:${mm}`;
}
function isMidnight(t: string): boolean {
  return t.length >= 16 && t.substring(11, 16) === '00:00';
}
function isSixHourMark(t: string): boolean {
  if (t.length < 16 || !t.substring(11, 16).endsWith(':00')) return false;
  const hh = parseInt(t.substring(11, 13), 10);
  return !Number.isNaN(hh) && hh % 6 === 0;
}
function xAxisLabel(t: string): string {
  return isMidnight(t) ? dayLbl(t) : t.substring(11, 16);
}

interface TooltipRow {
  color: string;
  label: string;
  value: string;
}

@Component({
  selector: 'app-combo-wind-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './combo-wind-chart.component.html',
  styleUrl: './combo-wind-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboWindChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() closeChart = new EventEmitter<void>();

  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('plot') plotRef!: ElementRef<HTMLDivElement>;

  readonly chartHeight = K_CHART_HEIGHT;

  state: ComboState | null = null;
  data: WindModelData | null = null;
  loading = false;
  error = false;
  series: ModelSeries[] = [];

  // scrub / scroll
  scrub: number | null = null;
  cursorY = 0;
  scrollX = 0;
  contentW = 0;
  tooltip: { left: number; top: number; header: string; rows: TooltipRow[] } | null = null;

  private lo = 0;
  private hi = 5;
  private sub?: Subscription;

  // pointer state for scrub vs swipe
  private pointerDown = false;
  private downX = 0;
  private downScroll = 0;
  private holdTimer: any = null;
  private scrubbing = false;

  constructor(
    public combo: ComboService,
    private cdr: ChangeDetectorRef,
    private userSettings: UserSettingsService,
  ) {}

  /**
   * Model wind speeds arrive in knots. The chart's geometry stays in knots (the
   * axis, the gridlines and the series are all scaled from it); only the numbers
   * shown to the viewer are converted, so a Speed preference of mph relabels the
   * axis and tooltips without redrawing the curve.
   */
  private knotsValue(knots: number, decimals = 0): string {
    return speedFromMs(knots * 0.514444, this.userSettings.speedUnit, decimals).value;
  }

  /** The viewer's speed-unit caption ("kts" / "mph" / "km/h"). */
  private get speedUnitLabel(): string {
    return speedFromMs(0, this.userSettings.speedUnit, 0).unit;
  }

  ngOnInit(): void {
    this.sub = this.combo.state$.subscribe((s) => {
      this.state = s;
      this.data = s.windModels;
      this.loading = s.windModelsLoading;
      this.error = s.windModelsError;
      this.series = (s.windModels?.series ?? []).filter((m) => !m.isAllNull);
      this.recomputeDomain();
      this.cdr.markForCheck();
      // redraw after view settles
      setTimeout(() => this.draw());
    });
  }

  ngAfterViewInit(): void {
    this.draw();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.holdTimer) clearTimeout(this.holdTimer);
  }

  get hasChart(): boolean {
    return this.data != null && this.series.length > 0 && this.data.times.length > 0;
  }

  // ── Header text ─────────────────────────────────────────────────────────────
  get subtitle(): string {
    if (this.loading) return 'loading…';
    if (this.error) return 'wind warming up';
    const data = this.data;
    if (data == null) return 'Drag the probe, then "compare models".';
    const stale =
      data.stale && data.updated != null ? `  ·  as of ${timeLbl(data.updated)}` : '';
    return `${data.cellLat.toFixed(1)}, ${data.cellLon.toFixed(1)}  ·  10 m wind  ·  ${data.snapDeg}° cell${stale}`;
  }

  onClose(): void {
    this.closeChart.emit();
  }
  onRetry(): void {
    const p = this.state?.probe;
    if (p) this.combo.loadWindModels(p.lat, p.lon);
  }

  // ── Domain + geometry ──────────────────────────────────────────────────────
  private recomputeDomain(): void {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of this.series) {
      for (const v of s.y) {
        if (v != null) {
          lo = Math.min(lo, v);
          hi = Math.max(hi, v);
        }
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      lo = 0;
      hi = 5;
    }
    this.lo = Math.max(0, Math.floor(lo / 5) * 5);
    this.hi = Math.ceil(hi / 5) * 5;
  }

  private get viewportW(): number {
    return this.plotRef?.nativeElement.clientWidth ?? 0;
  }

  // ── Canvas draw ──────────────────────────────────────────────────────────────
  private draw(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.hasChart || !this.data) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vw = this.viewportW;
    const n = this.data.times.length;
    // Full-width bottom sheet: fit the whole forecast in the visible viewport
    // (no horizontal scroll) as long as each sample gets ≥6 px. Only fall back
    // to a scrolling wider canvas on very narrow screens where samples would
    // otherwise be sub-pixel.
    const MIN_STEP_PX = 6;
    const naturalW = K_LM + K_RM + n * MIN_STEP_PX;
    this.contentW = Math.max(vw, naturalW);

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(vw * dpr) || canvas.height !== Math.round(K_CHART_HEIGHT * dpr)) {
      canvas.width = Math.round(vw * dpr);
      canvas.height = Math.round(K_CHART_HEIGHT * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, vw, K_CHART_HEIGHT);

    const pw = vw - K_LM - K_RM;
    const ph = K_CHART_HEIGHT - K_TM - K_BM;
    const pwFull = this.contentW - K_LM - K_RM;
    const sx = this.scrollX;
    const lo = this.lo;
    const hi = this.hi;
    const times = this.data.times;

    const xOf = (i: number) =>
      K_LM + pwFull * (n <= 1 ? 0 : i / (n - 1)) - sx;
    const yOf = (v: number) => K_TM + ph * (1 - (v - lo) / (hi - lo === 0 ? 1 : hi - lo));

    // Fixed left axis: y gridlines + labels + "kn".
    ctx.strokeStyle = '#1D3148';
    ctx.lineWidth = 1;
    ctx.fillStyle = '#9FB6CC';
    ctx.font = '9px sans-serif';
    ctx.textBaseline = 'alphabetic';
    for (let k = 0; k <= 4; k++) {
      const v = lo + (hi - lo) * (k / 4);
      const y = yOf(v);
      ctx.beginPath();
      ctx.moveTo(K_LM, y);
      ctx.lineTo(K_LM + pw, y);
      ctx.stroke();
      ctx.textAlign = 'right';
      ctx.fillText(this.knotsValue(v), K_LM - 4, y - 2);
    }
    ctx.textAlign = 'left';
    ctx.fillText(this.speedUnitLabel, vw - K_RM - 14, K_TM + 6);

    // Scrolling content, clipped to the plot area.
    ctx.save();
    ctx.beginPath();
    ctx.rect(K_LM, 0, pw, K_CHART_HEIGHT);
    ctx.clip();

    // x-axis 6-hour gridlines + labels.
    for (let i = 0; i < n; i++) {
      if (!isSixHourMark(times[i])) continue;
      const x = xOf(i);
      if (x < K_LM - 1 || x > K_LM + pw + 1) continue;
      const midnight = isMidnight(times[i]);
      ctx.strokeStyle = midnight ? '#16273A' : 'rgba(22, 39, 58, 0.5)';
      ctx.beginPath();
      ctx.moveTo(x, K_TM);
      ctx.lineTo(x, K_TM + ph);
      ctx.stroke();
      ctx.fillStyle = '#9FB6CC';
      ctx.textAlign = 'left';
      ctx.fillText(xAxisLabel(times[i]), x + 2, K_TM + ph + 12);
    }

    // series polylines (null gaps break the line).
    for (const s of this.series) {
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      let started = false;
      for (let i = 0; i < n && i < s.y.length; i++) {
        const v = s.y[i];
        if (v == null) {
          if (started) {
            ctx.stroke();
            ctx.beginPath();
            started = false;
          }
          continue;
        }
        const x = xOf(i);
        const y = yOf(v);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
      if (started) ctx.stroke();
    }

    // scrub crosshair.
    const i = this.scrub;
    if (i != null && i >= 0 && i < n) {
      const x = xOf(i);
      if (x >= K_LM - 1 && x <= K_LM + pw + 1) {
        ctx.strokeStyle = 'rgba(159, 182, 204, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, K_TM);
        ctx.lineTo(x, K_TM + ph);
        ctx.moveTo(K_LM, this.cursorY);
        ctx.lineTo(K_LM + pw, this.cursorY);
        ctx.stroke();
      }
    }
    ctx.restore();

    this.updateTooltip(vw, pwFull, ph);
  }

  private updateTooltip(vw: number, pwFull: number, ph: number): void {
    const i = this.scrub;
    const data = this.data;
    if (i == null || data == null) {
      this.tooltip = null;
      return;
    }
    const n = data.times.length;
    const sx = this.scrollX;
    const xI = K_LM + pwFull * (n <= 1 ? 0 : i / (n - 1)) - sx;
    const vMouse = this.lo + (1 - (this.cursorY - K_TM) / ph) * (this.hi - this.lo);
    const boxW = 158;
    const left = Math.max(K_LM, Math.min(Math.max(K_LM, vw - boxW - 4), xI + 14));
    const estH = 30 + this.series.length * 13;
    const maxTop = Math.max(0, K_CHART_HEIGHT - K_BM - estH);
    const top = Math.max(0, Math.min(maxTop, this.cursorY - 10));
    const rows: TooltipRow[] = this.series.map((s) => {
      const y = i < s.y.length ? s.y[i] : null;
      return {
        color: s.color,
        label: s.label,
        value: y == null ? '—' : `${this.knotsValue(y)} ${this.speedUnitLabel}`,
      };
    });
    this.tooltip = {
      left,
      top,
      header: `${timeLbl(data.times[i])} · cursor ${this.knotsValue(vMouse)} ${this.speedUnitLabel}`,
      rows,
    };
    this.cdr.markForCheck();
  }

  // ── Pointer interaction (scrub vs swipe) ──────────────────────────────────
  onPointerDown(ev: PointerEvent): void {
    if (!this.hasChart) return;
    this.pointerDown = true;
    this.scrubbing = false;
    this.downX = ev.clientX;
    this.downScroll = this.scrollX;
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    if (this.holdTimer) clearTimeout(this.holdTimer);
    this.holdTimer = setTimeout(() => {
      this.scrubbing = true;
      this.setScrubFromEvent(ev);
    }, 220);
  }

  onPointerMove(ev: PointerEvent): void {
    if (!this.pointerDown) return;
    const dx = ev.clientX - this.downX;
    if (this.scrubbing) {
      this.setScrubFromEvent(ev);
      return;
    }
    if (Math.abs(dx) > 6) {
      // swipe → scroll, cancel pending hold
      if (this.holdTimer) {
        clearTimeout(this.holdTimer);
        this.holdTimer = null;
      }
      const maxScroll = Math.max(0, this.contentW - this.viewportW);
      this.scrollX = Math.max(0, Math.min(maxScroll, this.downScroll - dx));
      this.scrub = null;
      this.tooltip = null;
      this.draw();
    }
  }

  onPointerUp(ev: PointerEvent): void {
    if (!this.pointerDown) return;
    const dx = ev.clientX - this.downX;
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
    // a plain tap (little movement, not yet scrubbing) places the crosshair
    if (!this.scrubbing && Math.abs(dx) <= 6) {
      this.scrubbing = true;
      this.setScrubFromEvent(ev);
    }
    this.pointerDown = false;
    this.scrubbing = false;
  }

  private setScrubFromEvent(ev: PointerEvent): void {
    const rect = this.plotRef.nativeElement.getBoundingClientRect();
    const px = ev.clientX - rect.left;
    const py = ev.clientY - rect.top;
    const n = this.data?.times.length ?? 0;
    const pwFull = this.contentW - K_LM - K_RM;
    const dataX = px + this.scrollX;
    const i =
      pwFull <= 0 || n <= 1
        ? 0
        : Math.max(0, Math.min(n - 1, Math.round(((dataX - K_LM) / pwFull) * (n - 1))));
    this.scrub = i;
    this.cursorY = Math.max(K_TM, Math.min(K_CHART_HEIGHT - K_BM, py));
    this.draw();
  }
}
